import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, signal, WritableSignal, computed, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { AttachmentViewerComponent } from '@app/lib/components/attachment-viewer/attachment-viewer.component';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { IBreadcrumb } from '@app/lib/models/breadcrumb.model';
import { TAny } from '@app/lib/models/common.type';
import { IAttachmentDialogData } from '@app/lib/models/dialog-data.model';
import { IResponse } from '@app/lib/models/response.model';
import { DialogService } from '@app/lib/services/dialog.service';
import { debounceTime, filter, switchMap } from 'rxjs';
import { ETaskScreenName, IAssignedTask, IAssignedTaskEvent } from '../models/assigned-task.model';
import { ICaseItem, ICaseParty, ICasePartyParams, ICaseSearchRequestBody, IPartyTypeModel, ISearchRequestParams } from '../models/case-assignment.model';
import { ECasePartyRoleType } from '../models/constant';
import { IHPAttachment, IHPCaseData, IHpCaseDocumentType, IHPCaseSearchRequestBody, RULING_DOCTYPE_NAME } from '../models/hearing-process.model';
import { AssignedTaskService } from '../services/assigned-task.service';
import { HearingProcessService } from '../services/hearing-process.service';
import { DialogMessageComponent } from '../dialog-message/dialog-message.component';
import { ILoginData } from '@app/lib/models/login.model';
import { ECourtId } from '@app/dashboard/models/constant';
import { DashboardComponent } from '../dashboard.component';
import { APP_STRING } from '@app/shared/shared.constants';
import { CaseNumberSearchComponent } from '../widget/case-number-search/case-number-search.component';

@Component({
  selector: 'app-hearing-process',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    BreadcrumbComponent,
    MatDatepickerModule,
    ScrollingModule,
    MatProgressBarModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    CaseNumberSearchComponent
  ],
  templateUrl: './hearing-process.component.html',
  styleUrl: './hearing-process.component.scss'
})
export class HearingProcessComponent {
  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([

  ]);


  public today: Date = new Date();

  displayedColumns: string[] = ['docName', 'timeCreated'];

  public partyRoleType = ECasePartyRoleType;

  public searchFormGrp: FormGroup = new FormGroup({
    docTypeId: new FormControl(''),
    caseNumber: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  });

  public formGrp: FormGroup = new FormGroup({
    docTypeId: new FormControl('', [Validators.required]),
    casePhaseId: new FormControl('', [Validators.required]),
    motionDocumentStatus: new FormControl(''),
    comments: new FormControl(''),
    docTypeName: new FormControl(''),
    cptName: new FormControl(''),
    judgementDate: new FormControl('')
  });

  public docFormGrp: FormGroup = new FormGroup({
    docUploadDateReceived: new FormControl(this.today, [Validators.required]),
    file: new FormControl('', [Validators.required]),
    selectedFile: new FormControl('', [Validators.required])
  });

  public caseListLoader: WritableSignal<boolean> = signal(false);
  public caseList: WritableSignal<ICaseItem[]> = signal([]);
  public task: WritableSignal<IAssignedTask | null> = signal(null);
  public docTypes: WritableSignal<IHpCaseDocumentType[]> = signal([]);
  public caseData: WritableSignal<IHPCaseData | null> = signal(null);
  public attachmentsData: WritableSignal<IHPAttachment[]> = signal([]);
  public casePhases: WritableSignal<TAny[]> = signal([]);
  public caseCategoryName: WritableSignal<'Case' | 'Appeal'> = signal('Case');
  public loading: WritableSignal<boolean> = signal(false);
  public partyDetails: WritableSignal<IPartyTypeModel> = signal({
    [this.partyRoleType.APPELLANT]: { value: [], partyData: [] },
    [this.partyRoleType.RESPONDENT]: { value: [], partyData: [] },
    loading: false
  });
  public caseAssigned: WritableSignal<boolean> = signal(false);
  public isEdit: WritableSignal<boolean> = signal(false);
  public docTypesByCaseNo: WritableSignal<IHpCaseDocumentType[]> = signal([]);
  public status: string[] = ['Granted', 'Refused', 'Strucked Out', 'Withdrawn'];
  public isRulingDocType: WritableSignal<boolean> = signal(false);
  public docTypeName: string | null = null; // Assume this holds the documentTypeName
  public isDocTypeReadonly: boolean = false;// if documentTypeName not exists in the provided list.
  public originalDocTypes: WritableSignal<IHpCaseDocumentType[]> = signal([]); // Backup of original document types
  public backupFormState: any = null; // To store the form values before edit
  public backupDocTypes: IHpCaseDocumentType[] = []; // To store the dropdown list before edit
  isCommentsRequired: boolean = false;
  isJudgement: boolean = false;
  minDate: Date = new Date();
  public uploadLoading: WritableSignal<boolean> = signal(false);
  isSupremeCourtFlg: boolean = false;
  isCOAFlg: boolean = false;
  courtData: ILoginData | null = null;
  strings = APP_STRING;

  userType: string = "";

  constructor(
    private service: HearingProcessService,
    private assignTaskService: AssignedTaskService,
    private mtDialog: MatDialog,
    private dialogService: DialogService,
    private dashboard: DashboardComponent

  ) {
    this.searchbyCaseNoChange();
    this.getDocTypes();
    this.assignTaskService.assignTask$
      .pipe(
        takeUntilDestroyed(),
        filter(event => event.name === ETaskScreenName.HEARINGPROCESS)
      )
      .subscribe({
        next: (event: IAssignedTaskEvent) => {
          this.task.set(event.task);
          this.searchFormGrp.reset({
            caseNumber: event.task.courtCaseNumber
          });
          this.searchHandler(event.task.courtCaseNumber);
        }
      });

    effect(() => {
      const filtered = this.filteredDocTypes();
      const currentDocTypeId = this.formGrp.get('docTypeId')?.value;
      if (filtered.length === 1 && !currentDocTypeId) {
        this.handleDocTypeSelection(filtered[0].docTypeId);
      }
    }, { allowSignalWrites: true });

  }

  ngOnInit(): void {
    this.loadCourtData();

  }
  public searchHandler(caseNumber?: string) {
    if (caseNumber === undefined) {
      this.task.set(null);
    }
    this.isJudgement = false;
    if (this.searchFormGrp.valid) {
      const docTypeId = this.searchFormGrp.value.docTypeId || '';
      const reqBody: IHPCaseSearchRequestBody = {
        courtCaseNumber: caseNumber || this.searchFormGrp.value.caseNumber,
        docTypename: '',
        docTypeId
      };
      // Reset the form and state
      this.caseAssigned.set(false); // Disable Save/Cancel
      this.formGrp.reset(); // Clear the form
      this.docTypesByCaseNo.set([]); // Clear the dropdown values
      this.casePhases.set([]); // Reset case phases
      this.backupFormState = null; // Clear the backup
      this.backupDocTypes = []; // Clear the backup

      this.getCaseData(reqBody);
      this.getPartyDetails(reqBody.courtCaseNumber);
      this.formGrp.disable();

    }
  }

  public resetHandler() {
    this.searchFormGrp.reset();
    this.caseData.set(null);
  }

  public assignHandler() {
    this.formGrp.markAllAsTouched();

    const docTypeId = this.formGrp.get('docTypeId')?.value;
    const casePhaseId = this.formGrp.get('casePhaseId')?.value;
    const motionDocumentStatus = this.formGrp.get('motionDocumentStatus')?.value;
    const comments = this.formGrp.get('comments')?.value;

    // 1. Required: docTypeId and casePhaseId
    if (!docTypeId || !casePhaseId) {
      this.dialogService.openDialog1({
        data: {
          title: 'Validation Error',
          message: 'Document Type and Case Phase Type are required.',
          icon: 'warning'
        }, disableClose: true,           // Prevents closing on outside click or ESC
        hasBackdrop: true,            // Shows backdrop to block interaction
        backdropClass: 'dialog-backdrop', // Optional: custom styling
      });
      return;
    }

    // 2. If docTypeId === 24, motionDocumentStatus is required
    if (docTypeId === 24 && !motionDocumentStatus) {
      this.dialogService.openDialog1({
        data: {
          title: 'Validation Error',
          message: 'Motion Document Status is required for selected document type.',
          icon: 'warning'
        }, disableClose: true,           // Prevents closing on outside click or ESC
        hasBackdrop: true,            // Shows backdrop to block interaction
        backdropClass: 'dialog-backdrop', // Optional: custom styling
      });
      return;
    }
    if (docTypeId === 27 && !comments) {
      this.dialogService.openDialog1({
        data: {
          title: 'Validation Error',
          message: 'Comments is required for selected document type.',
          icon: 'warning'
        }, disableClose: true,           // Prevents closing on outside click or ESC
        hasBackdrop: true,            // Shows backdrop to block interaction
        backdropClass: 'dialog-backdrop', // Optional: custom styling
      });
      return;
    }

    const reqBody: TAny = {
      "courtUnitName": this.caseData()?.courtUnitName,
      "courtUnitId": this.caseData()?.courtUnitId,
      "appealCourtName": this.caseData()?.appealCourtName,
      "appealCourtId": this.caseData()?.appealCourtId,
      "ctpShortName": this.caseData()?.ctpShortName,
      "ctpId": this.caseData()?.ctpId,
      "docTypename": this.formGrp.get('docTypeName')?.value,
      "docTypeId": docTypeId,
      "cptShortName": this.formGrp.get('cptName')?.value,
      "cptId": casePhaseId,
      "motionDocumentStatus": motionDocumentStatus,
      "partiesByPrtIdReallyIssuedBy": this.caseData()?.partiesByPrtIdReallyIssuedBy,
      "partiesByPrtIdReallyIssuedByName": this.caseData()?.partiesByPrtIdReallyIssuedByName,
      "partiesByPrtIdIssuedBy": this.caseData()?.partiesByPrtIdIssuedBy,
      "partiesByPrtIdIssuedByName": this.caseData()?.partiesByPrtIdIssuedByName,
      "partyIdCharging": this.caseData()?.partyIdCharging,
      "partyIdChargingName": this.caseData()?.partyIdChargingName,
      "comments": comments,
      "courtCaseNumber": this.caseData()?.courtCaseNumber,
      "casePhaseId": this.caseData()?.casePhaseId,
      "docId": this.caseData()?.docId,
      "casePhaseCasId": this.caseData()?.casePhaseCasId,
      "restrictionName": this.caseData()?.restrictionName,
      "restrictionId": this.caseData()?.restrictionId,
      "receiptTypeName": this.caseData()?.receiptTypeName,
      "receiptTypeId": this.caseData()?.receiptTypeId,
      "numberOfCopies": this.caseData()?.numberOfCopies,
      "dateReceived": this.caseData()?.dateReceived,
      "dateMailed": this.caseData()?.dateMailed,
      "oldCaseNumber": this.caseData()?.oldCaseNumber,
      "suitNumber": this.caseData()?.suitNumber,
      "hearingProcessDocTypeId": this.caseData()?.hearingProcessDocTypeId,
      "hearingProcessCasePhTypeId": this.caseData()?.hearingProcessCasePhTypeId,
      "rulingDocTypeId": this.caseData()?.rulingDocTypeId,
      "rulingCasePhTypeId": this.caseData()?.rulingCasePhTypeId,
      "caseCategory": this.caseData()?.caseCategory,
      "triggeredTaskId": this.caseData()?.triggeredTaskId,
      "hearingCourtUnitName": this.caseData()?.hearingCourtUnitName,
      "hearingCourtUnitId": this.caseData()?.hearingCourtUnitId,
      "transType": "I",

    };
    this.service.upsertDocument(reqBody).subscribe({
      next: (res: IResponse<TAny>) => {
        this.caseAssigned.set(false);
        this.isEdit.set(false)
        this.formGrp.disable();
        const dialogRef = this.dialogService.openDialog1({
          data: {
            title: "Information",
            message: res.data?.validString || '',
            icon: 'check_small', // ✅ Default icon if not provided
          }, disableClose: true,           // Prevents closing on outside click or ESC
          hasBackdrop: true,            // Shows backdrop to block interaction
          backdropClass: 'dialog-backdrop', // Optional: custom styling
        });
        dialogRef.afterClosed().subscribe(() => {
          // You can add additional logic here to open another dialog after the first closes.
          const dialogRef1 = this.dialogService.openDialog1({
            data: {
              title: "Information",
              message: 'You may now attach any documents if required',
              icon: 'warning', // ✅ Default icon if not provided
            }, disableClose: true,           // Prevents closing on outside click or ESC
            hasBackdrop: true,            // Shows backdrop to block interaction
            backdropClass: 'dialog-backdrop', // Optional: custom styling
          });
          dialogRef1.afterClosed().subscribe(() => {
            this.dashboard.refreshSidebarTasks();
            this.searchFormGrp.patchValue({
              caseNumber: res.data?.courtCaseNumber,
            });
            this.task.set(null)
            this.searchHandler(res.data?.courtCaseNumber)
          });

        });
        // Clear backups after successful save
        this.backupFormState = null;
        this.backupDocTypes = [];
        this.searchHandler();
      }
    });
  }

  loadCourtData(): void {
    this.courtData = this.getCourtNameData();
    this.userType = this.courtData?.courtId === 1 || this.courtData?.courtId === 2 ? 'admin' : 'lawyer';
    if (this.courtData && this.courtData.courtId === ECourtId.SC_ID) {
      this.caseCategoryName.set('Appeal')
      this.breadcrumbs.set([{ label: 'Appeal' },
      { label: 'Hearing Process' },]);
      this.isSupremeCourtFlg = true;
    } else if (this.courtData && this.courtData.courtId === ECourtId.COA_ID) {
      this.breadcrumbs.set([{ label: 'Case' },
      { label: 'Hearing Process' },]);
      this.isCOAFlg = true;
    } else {
      this.breadcrumbs.set([{ label: 'Case' },
      { label: 'Hearing Process' },]);
    }
    //console.log('Loaded Court Data:', this.courtData);

  }

  public onCaseSearchOptionSelected(item: any): void {
    const caseNo = item?.caseNumber || item?.courtCaseNumber || '';
    if (caseNo) {
      this.searchFormGrp.patchValue({ caseNumber: caseNo });
    }
  }

  getCourtNameData(): ILoginData | null {
    try {
      const userData = localStorage.getItem('userData') as string;
      return userData ? JSON.parse(userData) : null;
    } catch (err) {
      return null
    }

  }

  public filteredDocTypes = computed(() => {
    const currentList = this.docTypesByCaseNo();
    const currentTask = this.task();

    // 1. Quick exit if no active task or no documents exist
    if (!currentTask || currentList.length === 0) {
      return currentList;
    }

    const taskTypeName = (currentTask as any).typeName.trim().toUpperCase();

    // 2. Helper to safely clean strings for comparison
    const normalize = (str: string) => (str || '').trim().toLowerCase();

    // 3. Define mapping configuration dynamically using your string registry
    const taskDocMapping: Record<string, string[]> = {
      "CONDUCT HEARING PROCESS": [
        this.strings.RECORD_OF_PROCEEDING,
        this.strings.FINAL_WRITTEN_ADOPTION,
        this.strings.COURT_PROCEEDINGS,
        this.strings.ENROLLED_ORDER,
        this.strings.FINAL_WRITTEN,
        this.strings.RESERVED_FOR_JUDGEMENT
      ],
      "CONDUCT MOTION HEARING PROCESS": [
        this.strings.FINAL_WRITTEN_ADOPTION_MOTION,
        this.strings.RECORD_OF_PROCEEDING_MOTION,
        this.strings.FINAL_WRITTEN_MOTION
      ],
      "PREPARE JUDGEMENT": [this.strings.JUDGEMENT],
      "RECORD DRAFT DRAWNUP ORDER": [this.strings.DRAFT_DRAWNUP_ORDER],
      "RECORD SIGNED DRAWNUP ORDER": [this.strings.SIGNED_DRAWNUP_ORDER],
      "RULING": [this.strings.RULING],
      "RECORD DRAFT ENROLLED ORDER": [this.strings.DRAFT_ENROLLED_ORDER],
      "RECORD SIGNED ENROLLED ORDER": [this.strings.SIGNED_ENROLLED_ORDER]
    };

    // 4. If the task type isn't defined in our mapping config, return all items early
    const allowedStrings = taskDocMapping[taskTypeName];
    if (!allowedStrings) {
      return currentList;
    }

    // 5. Build a normalized lookup Set for O(1) matching efficiency
    const allowedSet = new Set(allowedStrings.map(normalize));

    // 6. Perform the filtering
    const filteredList = currentList.filter(doc =>
      allowedSet.has(normalize(doc.docTypeName))
    );


    return filteredList.length > 0 ? filteredList : currentList;
  });
  public openAttachmentDialog(document: IHPAttachment) {
    const data: IAttachmentDialogData = {
      path: 'documents/download.htm',
      queryParams: { fileRef: document.alfrescoUrl }
    };

    this.mtDialog.open(AttachmentViewerComponent, {
      panelClass: 'dialog-pdf-viewer',
      data, disableClose: true,           // Prevents closing on outside click or ESC
      hasBackdrop: true,            // Shows backdrop to block interaction
      backdropClass: 'dialog-backdrop', // Optional: custom styling
    });
  }

  public editHandler() {
    this.isEdit.set(true);
    this.caseAssigned.set(true);
    this.formGrp.enable();

    // Backup the current form state and dropdown list BEFORE clearing
    this.backupFormState = { ...this.formGrp.getRawValue() };
    this.backupDocTypes = [...this.docTypesByCaseNo()];

    // Clear document type and case phase dropdown selections
    this.formGrp.get('docTypeId')?.setValue('');
    this.formGrp.get('casePhaseId')?.setValue('');
    this.formGrp.get('comments')?.setValue('');
    this.casePhases.set([]); // Clear case phases list

    // Reset validation and helper states
    this.isRulingDocType.set(false);
    this.isJudgement = false;
    this.isCommentsRequired = false;

    this.formGrp.get('comments')?.clearValidators();
    this.formGrp.get('comments')?.updateValueAndValidity();
    this.formGrp.get('judgementDate')?.clearValidators();
    this.formGrp.get('judgementDate')?.updateValueAndValidity();
    this.formGrp.get('motionDocumentStatus')?.clearValidators();
    this.formGrp.get('motionDocumentStatus')?.updateValueAndValidity();

    // Restore the original dropdown values
    this.docTypesByCaseNo.set([...this.originalDocTypes()]);

    // Check if the task restricts the document type selection, and auto-select it if so
    const currentTask = this.task();
    let selected = false;
    if (currentTask) {
      const taskTypeId = (currentTask as any).typeId;
      let autoDocTypeId: number | null = null;
      if (taskTypeId === 10) autoDocTypeId = 21;
      else if (taskTypeId === 11) autoDocTypeId = 19;
      else if (taskTypeId === 12) autoDocTypeId = 20;

      if (autoDocTypeId) {
        this.handleDocTypeSelection(autoDocTypeId);
        selected = true;
      }
    }
    if (!selected) {
      const filtered = this.filteredDocTypes();
      if (filtered.length === 1) {
        this.handleDocTypeSelection(filtered[0].docTypeId);
      }
    }
  }

  public cancelHandler() {
    this.isEdit.set(false);
    this.caseAssigned.set(false);
    this.formGrp.disable();

    // Restore the form state and dropdown list from the backup
    if (this.backupFormState) {
      this.formGrp.reset(this.backupFormState);

      // Restore helper states and validators based on the restored docTypeId
      const restoredDocTypeId = this.backupFormState.docTypeId;
      if (restoredDocTypeId) {
        const selectedDocType = this.docTypesByCaseNo().find(item => item.docTypeId === restoredDocTypeId);
        if (selectedDocType) {
          this.isRulingDocType.set(selectedDocType.docTypeName === RULING_DOCTYPE_NAME);
          // if (selectedDocType.docTypeName.toLowerCase().includes('judgement')) {
          //   this.isJudgement = true;
          // } else {
          //   this.isJudgement = false;
          // }
          if (selectedDocType.docTypeId === 27) {
            this.isCommentsRequired = true;
          } else {
            this.isCommentsRequired = false;
          }
        }
        // Fetch case phases for the restored docTypeId
        this.getCasePhases(restoredDocTypeId);
      } else {
        this.isRulingDocType.set(false);
        this.isJudgement = false;
        this.isCommentsRequired = false;
        this.casePhases.set([]);
      }
    }

    if (this.backupDocTypes.length) {
      this.docTypesByCaseNo.set([...this.backupDocTypes]);
    }

    // Clear the backups
    this.backupFormState = null;
    this.backupDocTypes = [];
  }

  public getCaseData(reqBody: IHPCaseSearchRequestBody) {
    this.caseData.set(null);
    this.attachmentsData.set([]);
    this.service.searchCaseDetails(reqBody)
      .subscribe({
        next: (res: IHPCaseData | null) => {
          if (res) {
            this.caseData.set(res);
            this.attachmentsData.set(res.attachmentsData || []);
            this.getDocTypesByCaseNo(res);
            // Ensure docTypeId exists in docTypesByCaseNo
            if (!this.docTypesByCaseNo().some(docType => docType.docTypeId === res.docTypeId) && res.docTypeId && res.docTypename) {
              this.docTypesByCaseNo.set([
                ...this.docTypesByCaseNo(),
                {
                  docTypeId: res.docTypeId,
                  docTypeName: res.docTypename,
                  ctpShortName: null,
                  cptShortName: null,
                  cptId: null,
                  ctpId: null,
                  transType: null,
                  parentDocId: null,
                  mandatory: null,
                  checklistFlag: null,
                  auditMessage: '', // Provide an empty string or default value
                },
              ]);
            }


            // Ensure casePhaseId exists in casePhases
            if (!this.casePhases().some(phase => phase.documentTypeCasePhaseTypeId === res.cptId) && res.cptId && res.cptShortName) {
              this.casePhases.set([
                ...this.casePhases(),
                {
                  documentTypeCasePhaseTypeId: res.cptId,
                  phaseName: res.cptShortName,
                },
              ]);
            }
            if (res.docTypename.toLowerCase().includes('judgement')) {
              this.isJudgement = true;
            }
            // Prepopulate the form
            this.formGrp.patchValue({
              docTypeId: res.docTypeId,
              casePhaseId: res.cptId,
              motionDocumentStatus: res.motionDocumentStatus || '',
              comments: res.comments || '',
              judgementDate: res.judgementDate || '',
            });

          }
        },
        error: (err) => {
          console.error('Error fetching case data:', err);
        },
      });
  }

  public caseDisplayFn(item: ICaseItem) {
    return item && item.caseNumber;
  }

  public handleDocTypeSelection(docTypeId: number) {
    this.formGrp.patchValue({ docTypeId });
    const validators = [];
    const selectedDocType = this.docTypesByCaseNo().find(item => item.docTypeId === docTypeId);
    if (selectedDocType) {
      this.isRulingDocType.set(selectedDocType.docTypeName === RULING_DOCTYPE_NAME);
      validators.push(Validators.required);
      this.formGrp.patchValue({
        docTypeName: selectedDocType.docTypeName, // Save the document name
      });
      if (selectedDocType.docTypeName.toLowerCase().includes('judgement')) {
        this.isJudgement = true;
        this.formGrp.get('judgementDate')?.setValidators([Validators.required]);
      } else {
        this.isJudgement = false;
        this.formGrp.get('judgementDate')?.clearValidators();
      }
      if (selectedDocType.docTypeId === 27) {
        this.formGrp.get('comments')?.setValidators([Validators.required]);
        this.isCommentsRequired = true;
      } else {
        this.formGrp.get('comments')?.clearValidators();
        this.isCommentsRequired = false;
      }
    } else {
      this.isRulingDocType.set(false);
      this.isJudgement = false;
      this.isCommentsRequired = false;
      this.formGrp.get('judgementDate')?.clearValidators();
      this.formGrp.get('comments')?.clearValidators();
    }

    this.getCasePhases(docTypeId);
    this.formGrp.controls['motionDocumentStatus'].setValidators(validators);
    this.formGrp.controls['motionDocumentStatus'].updateValueAndValidity();
    this.formGrp.get('judgementDate')?.updateValueAndValidity();
    this.formGrp.get('comments')?.updateValueAndValidity();
  }

  public docTypesByCaseNoChange(e: MatSelectChange) {
    this.formGrp.patchValue({ casePhaseId: '', motionDocumentStatus: '' });
    this.handleDocTypeSelection(e.value);
  }

  public onFileSelectionChange(e: Event) {
    const file = (e.target as HTMLInputElement)?.files?.[0];
    if (file) {
      this.docFormGrp.patchValue({ selectedFile: file });
    }
  }
  casePhaseChange(event: MatSelectChange) {
    const selectedCasePhase = this.casePhases().find(cp => cp.documentTypeCasePhaseTypeId === event.value);

    if (selectedCasePhase) {
      this.formGrp.patchValue({
        cptName: selectedCasePhase.phaseName, // Save the case phase name
      });
    }
  }
  isFileSelected(): boolean {
    const fileControl = this.docFormGrp.get('file');
    return fileControl?.value != null && fileControl?.value !== '';
  }
  onDateChange(event: any): void {
    const rawDate = event.value;
    if (rawDate) {
      this.formGrp.patchValue({ judgementDate: rawDate });

    }
  }
  public onFileUpload() {
    this.docFormGrp.markAllAsTouched();
    if (this.caseData() && this.docFormGrp.valid) {
      this.uploadLoading.set(true);
      const formData = new FormData();
      const file: File = this.docFormGrp.value.selectedFile;
      formData.append('file', file, file.name);
      formData.append('documentsData', '');
      const queryParams = {
        docId: this.caseData()?.docId,
        caseId: this.caseData()?.casePhaseCasId,
        casePhaseId: this.caseData()?.casePhaseId,
        dateReceived: this.caseData()?.dateReceived,
        docTypeID: this.caseData()?.docTypeId,
        caseNumber: this.caseData()?.courtCaseNumber
      };
      this.service.uploadFile(formData, queryParams).subscribe({
        next: (res: any) => {
          this.uploadLoading.set(false);
          this.docFormGrp.reset({
            docUploadDateReceived: this.today
          });
          const item = this.caseList().find(item => item.caseNumber === this.caseData()?.courtCaseNumber);
          this.searchFormGrp.reset({
            caseNumber: item?.caseNumber ?? ''
          });
          this.caseAssigned.set(false);
          this.searchHandler();
          if (res && res.success) {
            this.dialogService.openDialog1({
              data: {
                title: "Success",
                message: res.success,
                icon: 'success', // ✅ Default icon if not provided
              }, disableClose: true,           // Prevents closing on outside click or ESC
              hasBackdrop: true,            // Shows backdrop to block interaction
              backdropClass: 'dialog-backdrop', // Optional: custom styling
            });
          }
        },
        error: (err) => {
          this.uploadLoading.set(false);
          console.error('File upload failed:', err);
        }
      });
    }
  }


  private getPartyDetails(caseNumber: string) {
    this.partyDetails.update((data) => {
      data.loading = true;
      return data;
    });
    const reqParams: ICasePartyParams = {
      caseNumber
    }
    this.service.getPartyDetails(reqParams).subscribe({
      next: (res: IResponse<ICaseParty[]>) => {
        if (res.data.length) {
          const appellant = res.data.filter(item => item.partyRoleId == this.partyRoleType.APPELLANT);
          const respondant = res.data.filter(item => item.partyRoleId == this.partyRoleType.RESPONDENT);
          this.partyDetails.update((data) => {
            data[this.partyRoleType.APPELLANT] = {
              partyData: appellant,
              value: appellant.map(item => item.fullName)
            };
            data[this.partyRoleType.RESPONDENT] = {
              partyData: respondant,
              value: respondant.map(item => item.fullName)
            };
            data.loading = false;
            return data;
          });
        }
      }
    });
  }

  private searchbyCaseNoChange() {
    this.searchFormGrp.controls['caseNumber'].valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(val => {
          if (typeof val === 'string' && val.trim().length > 3) {
            this.caseListLoader.set(true);
            return true;
          }
          this.caseListLoader.set(false);
          return false;
        }),
        debounceTime(500),
        switchMap((searchVal: string) => this.searchCases(searchVal))
      )
      .subscribe({
        next: (res: IResponse<ICaseItem[]>) => {
          this.caseList.set(res.data);
          this.caseListLoader.set(false);
        },
        error: () => {
          this.caseListLoader.set(false);
        }
      });
  }

  private getDocTypes() {
    this.service.getDocumenTypes().subscribe({
      next: (res: IResponse<IHpCaseDocumentType[]>) => {
        const fetchedDocTypes = res.data || res.success || [];

        // Set docTypes with the fetched data
        this.docTypes.set(fetchedDocTypes);

        // Populate originalDocTypes only if it's empty
        if (this.originalDocTypes().length === 0) {
          this.originalDocTypes.set([...fetchedDocTypes]);
        }
      },
      error: (err) => {
        console.error('Error fetching document types:', err);
      }
    });
  }
  private getDocTypesByCaseNo(res: IHPCaseData) {
    const reqParams: TAny = {
      caseNumber: res.courtCaseNumber,
      appealTypeId: res.ctpId,
      caseId: res.casePhaseCasId
    };
    this.service.getDocumentTypesByCaseNo(reqParams).subscribe({
      next: (response: IResponse<IHpCaseDocumentType[]>) => {
        const fetchedDocTypes = response.data || response.success || [];

        this.originalDocTypes.set([...fetchedDocTypes]); // Save the original fetched types

        // Ensure autoDocTypeId exists in the fetched list if restricted by an active task
        const currentTask = this.task();
        let autoDocTypeId: number | null = null;
        if (currentTask) {
          const taskTypeId = (currentTask as any).typeId;
          if (taskTypeId === 10) autoDocTypeId = 21;
          else if (taskTypeId === 11) autoDocTypeId = 19;
          else if (taskTypeId === 12) autoDocTypeId = 20;
        }

        if (autoDocTypeId && !fetchedDocTypes.some(docType => docType.docTypeId === autoDocTypeId)) {
          let docTypeName = 'Unknown';
          const foundGeneral = this.docTypes().find(d => d.docTypeId === autoDocTypeId);
          if (foundGeneral) {
            docTypeName = foundGeneral.docTypeName;
          } else {
            if (autoDocTypeId === 21) docTypeName = 'Ruling';
            else if (autoDocTypeId === 19) docTypeName = 'Notice';
            else if (autoDocTypeId === 20) docTypeName = 'Order';
          }

          fetchedDocTypes.push({
            docTypeId: autoDocTypeId,
            docTypeName: docTypeName,
            ctpShortName: null,
            cptShortName: null,
            cptId: null,
            ctpId: null,
            transType: null,
            parentDocId: null,
            mandatory: null,
            checklistFlag: null,
            auditMessage: '',
          });
        }

        // Check if the current docTypeId exists in the fetched list
        const docTypeFld = this.formGrp.getRawValue().docTypeId;
        const existsInList = fetchedDocTypes.some((docType) => docType.docTypeId === docTypeFld);

        // Add the current docTypeId to the list if it doesn't exist
        if (docTypeFld && !existsInList) {
          fetchedDocTypes.push({
            docTypeId: docTypeFld,
            docTypeName: res.docTypename || 'Unknown',
            ctpShortName: null,
            cptShortName: null,
            cptId: null,
            ctpId: null,
            transType: null,
            parentDocId: null,
            mandatory: null,
            checklistFlag: null,
            auditMessage: '',
          });
        }

        // Update the signal with the updated list
        this.docTypesByCaseNo.set(fetchedDocTypes);

        if (docTypeFld) {
          const selectedDocType = this.docTypesByCaseNo().find(item => item.docTypeId === docTypeFld);
          if (selectedDocType) {
            this.isRulingDocType.set(selectedDocType.docTypeName === RULING_DOCTYPE_NAME);
          }
        }

        // Auto-select the document type if restricted by an active task or if filteredDocTypes has only 1 option
        const currentDocTypeId = this.formGrp.get('docTypeId')?.value;
        if (!currentDocTypeId) {
          if (autoDocTypeId) {
            this.handleDocTypeSelection(autoDocTypeId);
          } else {
            const filtered = this.filteredDocTypes();
            if (filtered.length === 1) {
              this.handleDocTypeSelection(filtered[0].docTypeId);
            }
          }
        }
      }
    });
  }

  private getCasePhases(docTypeId: number) {
    this.formGrp.get('casePhaseId')?.setValue(null);
    const params = {
      docTypeId
    };
    this.service.getCasePhases(params).subscribe({
      next: (res) => {
        this.casePhases.set(res.data || res.success || []);
        if (this.formGrp.getRawValue().docTypeId && this.casePhases().length === 1) {
          this.formGrp.patchValue({
            casePhaseId: this.casePhases()[0].documentTypeCasePhaseTypeId ?? '',
            cptName: this.casePhases()[0].phaseName ?? ''
          });
        }
      }
    })
  }

  private searchCases(val: string) {
    const body: ICaseSearchRequestBody = {
      caseNumber: val,
      dateOfFiling: 'null'
    };
    const params: ISearchRequestParams = {
      page: 1,
      start: 0,
      limit: 25,
      property: 'id',
      direction: 'ASC'
    }
    return this.service.searchCaseNumber(body, params);
  }


}
